import { useQuery } from "@tanstack/react-query";
import { PropsWithChildren, createContext, useContext, useMemo } from "react";

import { api, type TimeRangeSelectorSettings, type TimeZoneSettings } from "@/lib/api";
import { unwrapAxiosData } from "@/lib/api/query-utils";
import Timezone from "@/lib/timezone/Timezone";
import { keys as dashboardQueryKeys } from "@/features/shared/hooks/queries";
import TimeRange from "@/types/time-range";

import { WidgetRuntimeProvider } from "./widget-runtime-context";

/**
 * Auto-refresh interval for CMS previews: effectively off. The refresh machine
 * only starts its timer once a widget registers an action, and manual refresh
 * stays available; this large (but < 2^31 ms, so `setTimeout`-safe) interval
 * keeps previews from re-fetching tiles on a timer while editing.
 */
const PREVIEW_AUTO_REFRESH_OFF = 2 ** 31 - 1;

type WidgetPreviewSeed = {
  timeRange: TimeRange;
  timezone?: Timezone;
  /**
   * Raw time-range selector settings for the dashboard (default + selectable
   * options), for previews that expose a range picker. Undefined until loaded.
   */
  timeRangeSettings?: TimeRangeSelectorSettings;
};

/**
 * Seed the host's time dimension from the admin dashboard being edited,
 * mirroring the dashboard page ({@link DashboardPage}): a {@link TimeRange} from
 * the dashboard's time-range selector settings and a {@link Timezone} from its
 * time-zone settings. Unlike the published dashboard these settings are not on
 * the admin `Dashboard` object, so they are fetched per dashboard (same query
 * keys as the admin time-settings screen, so the cache is shared).
 *
 * The Highcharts timezone/weekday defaults are a *global* setter and so are
 * applied once higher up in the CMS route (see {@link useHighchartsTimezoneDefaults}
 * in the admin layout), not here per preview.
 *
 * Falls back to the default `TimeRange()` and no timezone when no dashboard id
 * is given or the settings have not loaded — e.g. outside a dashboard route.
 */
function useWidgetPreviewSeed(dashboardId: string | undefined): WidgetPreviewSeed {
  const timeRangeQuery = useQuery({
    queryKey: dashboardQueryKeys.timerange(String(dashboardId)),
    queryFn: (): Promise<TimeRangeSelectorSettings> =>
      unwrapAxiosData(
        api.timeRangeSelector.getTimeRangeSelectorSettingsForDashboard(String(dashboardId)),
      ),
    enabled: !!dashboardId,
  });

  const timezoneQuery = useQuery({
    queryKey: dashboardQueryKeys.timezone(String(dashboardId)),
    queryFn: (): Promise<TimeZoneSettings> =>
      unwrapAxiosData(api.timezones.getTimeZoneSettingsForDashboard(String(dashboardId))),
    enabled: !!dashboardId,
  });

  const timeRangeSettings = timeRangeQuery.data;
  const timezoneSettings = timezoneQuery.data;

  const timeRange = useMemo(() => {
    if (!timeRangeSettings) return new TimeRange();
    return new TimeRange(timeRangeSettings.default_start, timeRangeSettings.default_end);
  }, [timeRangeSettings]);

  const timezone = useMemo(
    () => Timezone.fromSettings(timezoneSettings),
    [timezoneSettings],
  );

  return { timeRange, timezone, timeRangeSettings };
}

type WidgetHostSettings = {
  /** Time-range selector options for the dashboard being previewed. */
  timeRangeSettings?: TimeRangeSelectorSettings;
};

const WidgetHostSettingsContext = createContext<WidgetHostSettings>({});

/** Host-provided settings (e.g. time-range options) for a-la-carte toolbar controls. */
export const useWidgetHostSettings = (): WidgetHostSettings =>
  useContext(WidgetHostSettingsContext);

type WidgetContextHostProps = {
  /** Admin dashboard being edited; seeds time range + timezone from its settings. */
  dashboardId: string | undefined;
  /**
   * Auto-refresh interval (ms). Defaults to effectively off for previews; the
   * host still takes over the refresh mechanism so `useRefreshAction` works and a
   * manual refresh control can trigger `refreshNow()`.
   */
  autoRefreshInterval?: number;
};

/**
 * Establishes an isolated, controllable widget runtime in the CMS so a dashboard
 * widget can be dropped in "as if on a dashboard". Seeds the time dimension from
 * the dashboard being edited (via {@link useWidgetPreviewSeed}) and provides the
 * same contexts the dashboard page does through the shared
 * {@link WidgetRuntimeProvider}: the widget runtime (scoped time range /
 * location + ambient timezone) and `RefreshContext` (refresh takeover — hosted
 * widgets' `useRefreshAction` registrations become live and can be triggered
 * manually). It adds host-only {@link WidgetHostSettingsContext} for a-la-carte
 * toolbar controls.
 */
export function WidgetContextHost({
  dashboardId,
  autoRefreshInterval = PREVIEW_AUTO_REFRESH_OFF,
  children,
}: PropsWithChildren<WidgetContextHostProps>) {
  const { timeRange, timezone, timeRangeSettings } = useWidgetPreviewSeed(dashboardId);

  return (
    <WidgetRuntimeProvider
      initialTimeRange={timeRange}
      timezone={timezone}
      autoRefreshInterval={autoRefreshInterval}
    >
      <WidgetHostSettingsContext.Provider value={{ timeRangeSettings }}>
        {children}
      </WidgetHostSettingsContext.Provider>
    </WidgetRuntimeProvider>
  );
}
